import { useState, useEffect, useRef } from "react";
import { useRouteLoaderData, useRevalidator, useFetcher } from "react-router";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff, User, Lock } from "lucide-react";
import {
	WORKSPACE_LOADER_ID,
	type WorkspaceLoaderData,
} from "@/routes/workspaceLoader";
import {
	WORKSPACE_SIDEBAR_ACTION_PATH,
	workspaceSidebarAction,
	type WorkspaceSidebarActionResult,
} from "@/routes/workspaceSidebarAction";

interface SettingsDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

export const SettingsDialog = ({ open, onOpenChange }: SettingsDialogProps) => {
	const layoutData = useRouteLoaderData(WORKSPACE_LOADER_ID) as
		| WorkspaceLoaderData
		| undefined;
	const { toast } = useToast();
	const revalidator = useRevalidator();
	const profile = layoutData?.profile ?? null;
	const userEmail = layoutData?.user?.email ?? "";
	const [username, setUsername] = useState(profile?.display_name ?? "");
	const [selectedModel, setSelectedModel] = useState(
		profile?.preferred_model ?? "openai/gpt-4o-mini",
	);

	// Password change states
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [showNewPassword, setShowNewPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const profileFetcher = useFetcher<typeof workspaceSidebarAction>();
	const passwordFetcher = useFetcher<typeof workspaceSidebarAction>();
	const profileResultRef = useRef<WorkspaceSidebarActionResult | null>(null);
	const passwordResultRef = useRef<WorkspaceSidebarActionResult | null>(null);

	const OPENROUTER_MODELS = [
		{ value: "openai/gpt-5-2025-08-07", label: "GPT-5 (Flagship)" },
		{
			value: "openai/gpt-5-mini-2025-08-07",
			label: "GPT-5 Mini (Fast & Efficient)",
		},
		{ value: "openai/gpt-5-nano-2025-08-07", label: "GPT-5 Nano (Fastest)" },
		{ value: "openai/gpt-4.1-2025-04-14", label: "GPT-4.1 (Reliable)" },
		{ value: "openai/o3-2025-04-16", label: "O3 (Reasoning)" },
		{ value: "openai/o4-mini-2025-04-16", label: "O4 Mini (Fast Reasoning)" },
		{ value: "openai/gpt-4o-mini", label: "GPT-4o Mini (Legacy)" },
		{ value: "openai/gpt-4o", label: "GPT-4o (Legacy)" },
		{ value: "anthropic/claude-3.5-sonnet", label: "Claude 3.5 Sonnet" },
		{ value: "anthropic/claude-3-haiku", label: "Claude 3 Haiku" },
		{ value: "google/gemini-2.5-pro", label: "Gemini 2.5 Pro" },
		{
			value: "meta-llama/llama-3.2-1b-instruct",
			label: "Meta Llama 3.2 1B Instruct",
		},
		{
			value: "microsoft/phi-3-mini-128k-instruct",
			label: "Microsoft: Phi-3 Mini 128K Instruct",
		},
		{ value: "meta-llama/llama-3.1-405b-instruct", label: "Llama 3.1 405B" },
		{ value: "meta-llama/llama-3.1-70b-instruct", label: "Llama 3.1 70B" },
		{ value: "meta-llama/llama-3.1-8b-instruct", label: "Llama 3.1 8B" },
		{ value: "meta-llama/llama-2-70b-chat", label: "Llama 2 70B Chat" },
		{ value: "meta-llama/llama-2-13b-chat", label: "Llama 2 13B Chat" },
	];

	useEffect(() => {
		setUsername(profile?.display_name ?? "");
		setSelectedModel(profile?.preferred_model ?? "openai/gpt-4o-mini");
	}, [profile?.display_name, profile?.preferred_model]);

	const submitAction = (
		fetcher: ReturnType<typeof useFetcher<typeof workspaceSidebarAction>>,
		intent: string,
		fields: Record<string, string | undefined>,
	) => {
		const formData = new FormData();
		formData.set("intent", intent);
		Object.entries(fields).forEach(([key, value]) => {
			if (value !== undefined) {
				formData.set(key, value);
			}
		});
		fetcher.submit(formData, {
			method: "post",
			action: WORKSPACE_SIDEBAR_ACTION_PATH,
		});
	};

	const saveUsername = () => {
		if (
			username === (profile?.display_name ?? "") &&
			selectedModel === (profile?.preferred_model ?? "openai/gpt-4o-mini")
		) {
			return;
		}

		submitAction(profileFetcher, "userUpdateProfile", {
			displayName: username.trim() || "",
			preferredModel: selectedModel,
		});
	};

	const handlePasswordChange = () => {
		if (!newPassword.trim()) {
			toast({
				title: "Password required",
				description: "Please enter a new password.",
				variant: "destructive",
			});
			return;
		}

		if (newPassword !== confirmPassword) {
			toast({
				title: "Passwords don't match",
				description: "Please ensure both passwords are the same.",
				variant: "destructive",
			});
			return;
		}

		if (newPassword.length < 6) {
			toast({
				title: "Password too short",
				description: "Password must be at least 6 characters long.",
				variant: "destructive",
			});
			return;
		}

		submitAction(passwordFetcher, "userUpdatePassword", {
			newPassword,
		});
	};

	const isProfilePending = profileFetcher.state !== "idle";
	const isPasswordPending = passwordFetcher.state !== "idle";

	useEffect(() => {
		if (profileFetcher.state !== "idle" || !profileFetcher.data) return;
		if (profileResultRef.current === profileFetcher.data) return;
		profileResultRef.current = profileFetcher.data;

		if (profileFetcher.data.ok) {
			toast({
				title: "Settings updated",
				description: "Your settings have been saved successfully.",
			});
			revalidator.revalidate();
		} else {
			toast({
				title: "Error saving settings",
				description: profileFetcher.data.error,
				variant: "destructive",
			});
		}
	}, [profileFetcher.state, profileFetcher.data, revalidator, toast]);

	useEffect(() => {
		if (passwordFetcher.state !== "idle" || !passwordFetcher.data) return;
		if (passwordResultRef.current === passwordFetcher.data) return;
		passwordResultRef.current = passwordFetcher.data;

		if (passwordFetcher.data.ok) {
			toast({
				title: "Password updated",
				description: "Your password has been changed successfully.",
			});
			setNewPassword("");
			setConfirmPassword("");
		} else {
			toast({
				title: "Error updating password",
				description: passwordFetcher.data.error,
				variant: "destructive",
			});
		}
	}, [passwordFetcher.state, passwordFetcher.data, toast]);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>Settings</DialogTitle>
					<DialogDescription>
						Configure your profile preferences and password.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-6">
					<Card>
						<CardHeader>
							<CardTitle className="flex items-center gap-2">
								<User className="h-4 w-4" />
								Profile
							</CardTitle>
							<CardDescription>
								Configure your profile information and display preferences.
							</CardDescription>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="space-y-2">
								<Label htmlFor="user-email">Email</Label>
								<Input
									id="user-email"
									type="email"
									value={userEmail}
									readOnly
									className="bg-muted text-muted-foreground"
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="username">Username</Label>
								<Input
									id="username"
									type="text"
									value={username}
									onChange={(e) => setUsername(e.target.value)}
									placeholder="Enter your username"
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="model">Preferred Model</Label>
								<Select value={selectedModel} onValueChange={setSelectedModel}>
									<SelectTrigger>
										<SelectValue placeholder="Select a model" />
									</SelectTrigger>
									<SelectContent className="bg-background border z-50">
										{OPENROUTER_MODELS.map((model) => (
											<SelectItem key={model.value} value={model.value}>
												{model.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<Button
								onClick={saveUsername}
								disabled={
									isProfilePending ||
									(username === (profile?.display_name || "") &&
										selectedModel ===
											(profile?.preferred_model || "openai/gpt-4o-mini"))
								}
							>
								{isProfilePending ? "Saving..." : "Save Settings"}
							</Button>
						</CardContent>
					</Card>

					<Card>
						<CardHeader>
							<CardTitle className="flex items-center gap-2">
								<Lock className="h-4 w-4" />
								Change Password
							</CardTitle>
							<CardDescription>Update your account password.</CardDescription>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="space-y-2">
								<Label htmlFor="newpassword">New Password</Label>
								<div className="relative">
									<Input
										id="newpassword"
										type={showNewPassword ? "text" : "password"}
										value={newPassword}
										onChange={(e) => setNewPassword(e.target.value)}
										placeholder="Enter new password"
										className="pr-10"
									/>
									<Button
										type="button"
										variant="ghost"
										size="sm"
										className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
										onClick={() => setShowNewPassword(!showNewPassword)}
									>
										{showNewPassword ? (
											<EyeOff className="h-3 w-3" />
										) : (
											<Eye className="h-3 w-3" />
										)}
									</Button>
								</div>
							</div>
							<div className="space-y-2">
								<Label htmlFor="confirmpassword">Confirm New Password</Label>
								<div className="relative">
									<Input
										id="confirmpassword"
										type={showConfirmPassword ? "text" : "password"}
										value={confirmPassword}
										onChange={(e) => setConfirmPassword(e.target.value)}
										placeholder="Confirm new password"
										className="pr-10"
									/>
									<Button
										type="button"
										variant="ghost"
										size="sm"
										className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
										onClick={() =>
											setShowConfirmPassword(!showConfirmPassword)
										}
									>
										{showConfirmPassword ? (
											<EyeOff className="h-3 w-3" />
										) : (
											<Eye className="h-3 w-3" />
										)}
									</Button>
								</div>
							</div>
							<Button
								onClick={handlePasswordChange}
								disabled={!newPassword || !confirmPassword || isPasswordPending}
							>
								{isPasswordPending ? "Updating..." : "Change Password"}
							</Button>
						</CardContent>
					</Card>
				</div>
			</DialogContent>
		</Dialog>
	);
};
